import { Header } from "./Components/Header/Header";
import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import "@mui/icons-material";
import { DrawioInterface } from "./DrawioClient/DrawioInterface";
import { SourceDoc } from "./Components/SourceDoc/SourceDoc";
import useStore from "./Store/Store";
import { SnackbarKey, useSnackbar } from "notistack";
import Dialogs from "./Components/Dialog/Dialogs";
import EditButton from "./Components/CanvasToolbar/EditButton";
import LandingComponent, {
  loadPublicRepo,
} from "./Components/LandingPage/LandingComponent";
import { checkPassportAuthentication } from "./Api/ServerInterface";
import AlternativeLandingComponent from "./Components/LandingPage/AlternativeLandingComponent";
import GitHubLoginSpotlight from "./Components/LandingPage/GitHubLoginSpotlight";
import {
  getURLParam,
  parseCurrentCodeCanvasRepoURL,
  removeURLParam,
  setURLParam,
} from "./utils/URLUtils";
import { loadDiagram } from "./Api/LoadDiagram";
import { DrawioEvent } from "./DrawioClient/DrawioTypes";
import DevTools from "./utils/DevTools";
import useStateChange from "./utils/useStateChange";
import { handleKeyDown } from "./utils/keyboardShortcuts";
import { CheckRunView } from "./Components/CheckRunView/CheckRunView";
import GuidedWizard from "./Components/GuidedWizard/GuidedWizard";
import onboardingConfig, {
  simulationOnboardingConfig,
} from "./utils/OnboardingData";
import BackToLandingPageButton from "./Components/CanvasToolbar/BackToLandingPageButton";
import { setupTestMode, simulateTestMode } from "./utils/TestUtils";
import { DiagramHighlight } from "./Components/DiagramHighlight/DiagramHighlight";
import ShapesButton from "./Components/CanvasToolbar/ShapesButton";
import ToolsButton from "./Components/CanvasToolbar/ToolsButton";
import ModelSelectionButton from "./Components/CanvasToolbar/ModelSelectionButton";
import ModelSelectionDialog from "./Components/Dialog/ModelSelectionDialog";
import { logger } from "./utils/Logger";
import CenterGraphButton from "./Components/CanvasToolbar/CenterGraphButton";
import { Fade } from "@mui/material";
import OnboardingWorkflow from "./Components/OnboardingWorkflow/OnboardingWorkflow";
import StatsWidget from "./Components/StatsWidget/StatsWidget";
import AutoDiagramGenerationOverlay from "./Components/OnboardingWorkflow/AutoDiagramGenerationOverlay";
import SimulationGenerationOverlay from "./Components/OnboardingWorkflow/SimulationGenerationOverlay";
import CellInsertionOverlay from "./Components/OnboardingWorkflow/CellInsertionOverlay";
import GlobalLoadingStatusBar from "./Components/OnboardingWorkflow/GlobalLoadingStatusBar";
import MobileLandingPage from "./Components/LandingPage/MobileLandingPage";
import FloatingStepDescriptionWindow from "./Components/SourceDoc/TabViews/SimulationsTab/FloatingStepDescriptionWindow";

// Import responsive design system
// import "./utils/responsive";
// import { useBreakpoint } from "./utils/responsive";

const REACT_APP_DRAWIO_URL = process.env.REACT_APP_DRAWIO_URL;
const DEMO_REPO = "react-ecommerce";
const App: React.FC = () => {
  const {
    drawioInterface,
    loadingNotification,
    errorNotification,
    successNotification,
    landingPageLoaded,
    doLoadDiagram,
    drawioClient,
    selectedSimulationKey,
    pausePathChangeToDrawio,
    debugMode,
    showHideIrrelevantCellsToggle,
    setErrorNotification,
    setSuccessNotification,
    setDrawioInterface,
    setSessionMode,
    repoData,
    removeFromFuse,
    addToFuse,
    checkRunImpactedCells,
    pausePathChangeToCodeCanvas,
    hideIrrelevantCells,
    considerParentsAsRelevantCells,
    isEditingSimulations,
    statsWidgetVisible,
    showOnboardingModelSelectionDialog,
    setShowOnboardingModelSelectionDialog,
    setSelectedModel,
    incrementUserEventCount,
    isSimulationOnboardingActive,
    setIsSimulationOnboardingActive,
    shouldShowSimulationOnboarding,
    setShouldShowSimulationOnboarding,
    firstAISimulationName,
    floatingStepDescriptionVisible,
    setFloatingStepDescriptionVisible,
  } = useStore((state) => ({
    drawioInterface: state.drawioInterface,
    loadingNotification: state.notification.loading,
    errorNotification: state.notification.error,
    successNotification: state.notification.success,
    landingPageLoaded: state.landingPageLoaded,
    doLoadDiagram: state.doLoadDiagram,
    drawioClient: state.drawioClient,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    pausePathChangeToDrawio: state.pausePathChangeToDrawio,
    debugMode: state.debugMode,
    showHideIrrelevantCellsToggle: state.showHideIrrelevantCellsToggle,
    setErrorNotification: state.setErrorNotification,
    setSuccessNotification: state.setSuccessNotification,
    setDrawioInterface: state.setDrawioInterface,
    setSessionMode: state.setSessionMode,
    repoData: state.repoData,
    removeFromFuse: state.removeFromFuse,
    addToFuse: state.addToFuse,
    checkRunImpactedCells: state.checkRunImpactedCells,
    pausePathChangeToCodeCanvas: state.pausePathChangeToCodeCanvas,
    hideIrrelevantCells: state.hideIrrelevantCells,
    considerParentsAsRelevantCells: state.considerParentsAsRelevantCells,
    isEditingSimulations: state.simulationsState.isEditingSimulations,
    statsWidgetVisible: state.statsWidgetVisible,
    showOnboardingModelSelectionDialog:
      state.showOnboardingModelSelectionDialog,
    setShowOnboardingModelSelectionDialog:
      state.setShowOnboardingModelSelectionDialog,
    setSelectedModel: state.setSelectedModel,
    incrementUserEventCount: state.incrementUserEventCount,
    isSimulationOnboardingActive: state.isSimulationOnboardingActive,
    setIsSimulationOnboardingActive: state.setIsSimulationOnboardingActive,
    shouldShowSimulationOnboarding: state.shouldShowSimulationOnboarding,
    setShouldShowSimulationOnboarding: state.setShouldShowSimulationOnboarding,
    firstAISimulationName: state.firstAISimulationName,
    floatingStepDescriptionVisible: state.floatingStepDescriptionVisible,
    setFloatingStepDescriptionVisible: state.setFloatingStepDescriptionVisible,
  }));
  const iFrameRef = useRef<HTMLIFrameElement>(null);
  //Dereference ToolBar function to access render
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loadingNotificationIds, setLoadingNotificationIds] = useState<
    SnackbarKey[]
  >([]);
  const historicalRepoData = useRef<Object>();
  const [codeLinkingModeNotifId, setCodeLinkingModeNotifId] =
    useState<SnackbarKey>(null);
  const [codeLinkToCanvasNotifId, setCodeLinkToCanvasNotifId] =
    useState<SnackbarKey>(null);

  // Sets codeLinkingMode notification
  useEffect(() => {
    if (useStore.getState().isPerformingUndoRedo) return;
    if (pausePathChangeToDrawio) {
      const notificationId = enqueueSnackbar(
        `Select code from the SourceDoc to link it to the selected cell! Any
      other diagram clicks will cancel.`,
        {
          variant: "snackBar",
          persist: true,
          preventDuplicate: true,
          anchorOrigin: { vertical: "top", horizontal: "center" },
          snackBarType: "codeLinkingMode",
        }
      );
      setCodeLinkingModeNotifId(notificationId);
    } else {
      closeSnackbar(codeLinkingModeNotifId);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pausePathChangeToDrawio]);

  useEffect(() => {
    if (useStore.getState().isPerformingUndoRedo) return;
    if (pausePathChangeToCodeCanvas) {
      const notificationId = enqueueSnackbar(
        "Select a diagram element to link to the selected code",
        {
          variant: "snackBar",
          persist: true,
          preventDuplicate: true,
          anchorOrigin: { vertical: "top", horizontal: "center" },
          snackBarType: "codeLinkingMode",
        }
      );
      setCodeLinkToCanvasNotifId(notificationId);
    } else {
      closeSnackbar(codeLinkToCanvasNotifId);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pausePathChangeToCodeCanvas]);

  // Sets and clears loading notification whenever it's set in zustand store
  useEffect(() => {
    if (useStore.getState().isPerformingUndoRedo) return;
    if (loadingNotification && loadingNotification.length > 0) {
      const notificationId = enqueueSnackbar(loadingNotification, {
        variant: "snackBar",
        persist: true,
        anchorOrigin: { vertical: "top", horizontal: "left" },
        snackBarType: "loading",
      });
      setLoadingNotificationIds((notifIdArr) => [
        ...notifIdArr,
        notificationId,
      ]);
    }
    if (
      loadingNotificationIds.length > 0 &&
      (!loadingNotification || loadingNotification.length === 0)
    ) {
      const notifIdToClose = loadingNotificationIds[-1];
      closeSnackbar(notifIdToClose);
      setLoadingNotificationIds((notifIdArr) => {
        notifIdArr.pop();
        return notifIdArr;
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingNotification]);

  // Sets error and success notifications whenever they are set in zustand store
  useEffect(() => {
    if (useStore.getState().isPerformingUndoRedo) return;
    if (errorNotification && errorNotification.length > 0) {
      enqueueSnackbar(errorNotification, {
        variant: "snackBar",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "left" },
        snackBarType: "error",
      });
      setErrorNotification("");
    }
    if (successNotification && successNotification.length > 0) {
      enqueueSnackbar(successNotification, {
        variant: "snackBar",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "left" },
        snackBarType: "success",
      });
      setSuccessNotification("");
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorNotification, successNotification]);

  // Sets and clears "In Simulation" motification whenever selectedSimulationKey changes in zustand store
  useEffect(() => {
    const isRunningSimulationNotifId =
      useStore.getState().simulationsState.isRunningSimulationNotifId;
    if (isRunningSimulationNotifId) {
      closeSnackbar(isRunningSimulationNotifId);
    }
    useStore.getState().setIsRunningSimulationNotifId(null);

    if (selectedSimulationKey) {
      const inSimNotificationId = enqueueSnackbar("In Simulation", {
        variant: "inSimulationSnackBar",
        persist: true,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      useStore.getState().setIsRunningSimulationNotifId(inSimNotificationId);
    }
  }, [selectedSimulationKey]);

  useEffect(() => {
    if (showHideIrrelevantCellsToggle) {
      // Check if there's already a snackbar open to prevent duplicates
      const existingNotificationId =
        useStore.getState().hideCellsToggleNotificationId;
      if (!existingNotificationId) {
        // Only create new snackbar if one doesn't already exist
        const toggleNotificationId = enqueueSnackbar("Level of Details", {
          variant: "LevelOfDetailsSlider",
          persist: true,
          anchorOrigin: { vertical: "top", horizontal: "center" },
          TransitionComponent: Fade,
        });
        useStore
          .getState()
          .setHideCellsToggleNotificationId(toggleNotificationId);
      }
    } else {
      const hideCellsToggleNotificationId =
        useStore.getState().hideCellsToggleNotificationId;
      if (hideCellsToggleNotificationId) {
        closeSnackbar(hideCellsToggleNotificationId);
        // Call the function to update drawio when toggle is closed
        hideIrrelevantCellsToggleAction(false, false);
      }
      useStore.getState().setHideCellsToggleNotificationId(null);
    }
  }, [showHideIrrelevantCellsToggle]);

  useEffect(() => {
    if (!drawioInterface) {
      setDrawioInterface(new DrawioInterface(iFrameRef));
    }
  }, [drawioInterface, setDrawioInterface]);

  useEffect(() => {
    function handleDrawioEvent(e) {
      if (e.origin !== process.env.REACT_APP_DRAWIO_URL) return;
      if (drawioClient) {
        drawioClient.handleEvent(JSON.parse(e.data as string) as DrawioEvent);
      }
    }
    window.addEventListener("message", handleDrawioEvent);
    return () => {
      window.removeEventListener("message", handleDrawioEvent);
    };
  }, [drawioClient]);

  // Handle CodeCanvas repository URLs separately and only once
  useEffect(() => {
    // Skip if already processed
    if (useStore.getState().URLParamParsingDone) {
      return;
    }

    // Check if current URL is a CodeCanvas repository URL
    const codeCanvasRepoInfo = parseCurrentCodeCanvasRepoURL(window);
    if (codeCanvasRepoInfo) {
      logger.debug.info(
        "CodeCanvas repository URL detected:",
        codeCanvasRepoInfo
      );

      // Mark URL param parsing as done first to prevent race conditions
      useStore.getState().setURLParamParsingDone(true);

      // Set repository info in store
      useStore.getState().setReposList([
        {
          name: codeCanvasRepoInfo.repo,
          owner: { login: codeCanvasRepoInfo.owner },
        },
      ]);

      // Extract model and regenerateWithPro parameters before cleaning up the URL
      const model = getURLParam(window, "model");
      const regenerateWithPro = getURLParam(window, "regenerateWithPro");

      // Clean up the URL
      const baseUrl = new URL(window.location.href);
      baseUrl.pathname = "/";
      baseUrl.search = `?repo=${codeCanvasRepoInfo.repo}&owner=${codeCanvasRepoInfo.owner}`;

      // Add model parameter to URL if it exists
      if (model) {
        baseUrl.search += `&model=${model}`;
        useStore.getState().setSelectedModel(model);
      }

      // Set regenerateWithPro flag if it exists (one-time use, don't add to URL)
      if (regenerateWithPro === "true") {
        useStore.getState().setRegenerateWithPro(true);
        // Remove the parameter from URL since it's one-time use
        removeURLParam(window, "regenerateWithPro");
      }

      baseUrl.hash = "";
      window.history.replaceState(null, "", baseUrl.toString());

      // Check if we have a stored username (indicating previous authentication)
      const storedUsername = localStorage.getItem("username");

      if (storedUsername) {
        logger.debug.info(
          "Found stored username for CodeCanvas URL, checking if user is still authenticated"
        );

        // Check authentication since we have a stored username
        checkPassportAuthentication()
          .then((authResult) => {
            if (authResult.isAuthenticated) {
              logger.debug.info(
                "User is authenticated, setting session mode to github"
              );

              // User is authenticated, set session mode to github
              setSessionMode("github");
              setURLParam(window, "session", "github");

              // Update store with authenticated user info
              if (authResult.user) {
                useStore.getState().setLoggedIn(true);

                // Store user info
                if (authResult.user.access_token) {
                  localStorage.setItem(
                    "accessToken",
                    authResult.user.access_token
                  );
                }
                if (authResult.user.username || authResult.user.login) {
                  localStorage.setItem(
                    "username",
                    authResult.user.username || authResult.user.login
                  );
                  useStore
                    .getState()
                    .setUsername(
                      authResult.user.username || authResult.user.login
                    );
                }
              }

              // Load with authentication
              loadPublicRepo(codeCanvasRepoInfo.githubURL, true).catch(
                (error) => {
                  logger.debug.warn(
                    "Repository loading failed with authentication:",
                    error
                  );
                  if (error.message === "PRIVATE_REPOSITORY") {
                    // Store the original URL for restoration after GitHub app installation
                    sessionStorage.setItem(
                      "originalCodeCanvasUrl",
                      window.location.href
                    );
                    useStore.getState().setShowGitHubLoginSpotlight(true);
                  }
                }
              );
            } else {
              logger.debug.info(
                "User no longer authenticated, continuing with unauthenticated mode"
              );

              // Clear stored credentials since they're no longer valid
              localStorage.removeItem("username");
              localStorage.removeItem("accessToken");

              // Set to unauthenticatedGithub mode
              setSessionMode("unauthenticatedGithub");
              setURLParam(window, "session", "unauthenticatedGithub");

              // Try to load as public repository
              loadPublicRepo(codeCanvasRepoInfo.githubURL, false).catch(
                (error) => {
                  logger.debug.warn(
                    "Repository loading failed in unauthenticated mode:",
                    error
                  );
                  if (error.message === "PRIVATE_REPOSITORY") {
                    logger.debug.info(
                      "Repository is private, showing GitHub login dialog"
                    );
                    // Store the original URL for restoration after GitHub app installation
                    sessionStorage.setItem(
                      "originalCodeCanvasUrl",
                      window.location.href
                    );
                    useStore.getState().setShowGitHubLoginSpotlight(true);
                  }
                }
              );
            }
          })
          .catch((error) => {
            logger.error("Error checking authentication:", error);

            // On error, continue with unauthenticatedGithub mode
            setSessionMode("unauthenticatedGithub");
            setURLParam(window, "session", "unauthenticatedGithub");

            // Try to load the repository in unauthenticated mode
            loadPublicRepo(codeCanvasRepoInfo.githubURL, false).catch(
              (loadError) => {
                if (loadError.message === "PRIVATE_REPOSITORY") {
                  // Store the original URL for restoration after GitHub app installation
                  sessionStorage.setItem(
                    "originalCodeCanvasUrl",
                    window.location.href
                  );
                  useStore.getState().setShowGitHubLoginSpotlight(true);
                }
              }
            );
          });
      } else {
        logger.debug.info(
          "No stored username found for CodeCanvas URL, proceeding with unauthenticated mode"
        );

        // No stored username, set to unauthenticatedGithub mode and try to load
        setSessionMode("unauthenticatedGithub");
        setURLParam(window, "session", "unauthenticatedGithub");

        // Try to load as public repository
        loadPublicRepo(codeCanvasRepoInfo.githubURL, false).catch((error) => {
          logger.debug.warn(
            "Repository loading failed in unauthenticated mode:",
            error
          );
          if (error.message === "PRIVATE_REPOSITORY") {
            logger.debug.info(
              "Repository is private, showing GitHub login dialog"
            );
            // Store the original URL for restoration after GitHub app installation
            sessionStorage.setItem(
              "originalCodeCanvasUrl",
              window.location.href
            );
            useStore.getState().setShowGitHubLoginSpotlight(true);
          }
        });
      }
      return;
    }
  }, []);

  // set session mode from url param
  useEffect(() => {
    // Skip if CodeCanvas URL was already processed
    if (useStore.getState().URLParamParsingDone) {
      return;
    }

    const testMode = getURLParam(window, "testMode");
    if (testMode === "1") {
      simulateTestMode(window);
    }
    setupTestMode(window);

    const urlParamSessionMode = getURLParam(window, "session");

    // Load repository for both github and unauthenticatedGithub sessions
    if (
      urlParamSessionMode === "github" ||
      urlParamSessionMode === "unauthenticatedGithub"
    ) {
      const repo = getURLParam(window, "repo");
      const branch = getURLParam(window, "branch");
      const owner = getURLParam(window, "owner");
      const model = getURLParam(window, "model");
      const regenerateWithPro = getURLParam(window, "regenerateWithPro");

      if (repo && owner) {
        // Set repository info first
        useStore
          .getState()
          .setReposList([{ name: repo, owner: { login: owner } }]);

        if (branch) {
          useStore.getState().setBranchesList([{ name: branch }]);
        }

        if (model) {
          useStore.getState().setSelectedModel(model);
        }

        // Set regenerateWithPro flag if it exists (one-time use)
        if (regenerateWithPro === "true") {
          useStore.getState().setRegenerateWithPro(true);
          // Remove the parameter from URL since it's one-time use
          removeURLParam(window, "regenerateWithPro");
        }

        // Check if we have a stored username (indicating previous authentication)
        const storedUsername = localStorage.getItem("username");

        if (storedUsername) {
          logger.debug.info(
            "Found stored username, checking if user is still authenticated"
          );

          // Check authentication since we have a stored username
          checkPassportAuthentication()
            .then((authResult) => {
              if (authResult.isAuthenticated) {
                logger.debug.info(
                  "User is still authenticated, upgrading session mode to github"
                );

                // User is authenticated, upgrade session mode to github
                setSessionMode("github");
                setURLParam(window, "session", "github");

                // Update store with authenticated user info
                if (authResult.user) {
                  useStore.getState().setLoggedIn(true);

                  // Store user info
                  if (authResult.user.access_token) {
                    localStorage.setItem(
                      "accessToken",
                      authResult.user.access_token
                    );
                  }
                  if (authResult.user.username || authResult.user.login) {
                    localStorage.setItem(
                      "username",
                      authResult.user.username || authResult.user.login
                    );
                    useStore
                      .getState()
                      .setUsername(
                        authResult.user.username || authResult.user.login
                      );
                  }
                }

                // Load with authentication
                let publicRepoURL = `https://github.com/${owner}/${repo}`;
                if (branch) {
                  publicRepoURL += `/tree/${branch}`;
                }

                loadPublicRepo(publicRepoURL, true).catch((error) => {
                  logger.debug.warn(
                    "Repository loading failed with authentication:",
                    error
                  );
                  if (error.message === "PRIVATE_REPOSITORY") {
                    // Store the original URL for restoration after GitHub app installation
                    sessionStorage.setItem(
                      "originalCodeCanvasUrl",
                      window.location.href
                    );
                    useStore.getState().setShowGitHubLoginSpotlight(true);
                  }
                });
              } else {
                logger.debug.info(
                  "User no longer authenticated, clearing stored credentials and continuing with unauthenticated mode"
                );

                // Clear stored credentials since they're no longer valid
                localStorage.removeItem("username");
                localStorage.removeItem("accessToken");

                // Continue with unauthenticatedGithub mode
                setSessionMode("unauthenticatedGithub");
                setURLParam(window, "session", "unauthenticatedGithub");

                // Try to load as public repository
                let publicRepoURL = `https://github.com/${owner}/${repo}`;
                if (branch) {
                  publicRepoURL += `/tree/${branch}`;
                }

                loadPublicRepo(publicRepoURL, false).catch((loadError) => {
                  if (loadError.message === "PRIVATE_REPOSITORY") {
                    // Store the original URL for restoration after GitHub app installation
                    sessionStorage.setItem(
                      "originalCodeCanvasUrl",
                      window.location.href
                    );
                    useStore.getState().setShowGitHubLoginSpotlight(true);
                  }
                });
              }
            })
            .catch((error) => {
              logger.error("Error checking authentication:", error);

              // On error checking auth, continue with unauthenticatedGithub mode
              setSessionMode("unauthenticatedGithub");
              setURLParam(window, "session", "unauthenticatedGithub");

              // Try to load the repository in unauthenticated mode
              let publicRepoURL = `https://github.com/${owner}/${repo}`;
              if (branch) {
                publicRepoURL += `/tree/${branch}`;
              }

              loadPublicRepo(publicRepoURL, false).catch((loadError) => {
                if (loadError.message === "PRIVATE_REPOSITORY") {
                  // Store the original URL for restoration after GitHub app installation
                  sessionStorage.setItem(
                    "originalCodeCanvasUrl",
                    window.location.href
                  );
                  useStore.getState().setShowGitHubLoginSpotlight(true);
                }
              });
            });
        } else {
          logger.debug.info(
            "No stored username found, proceeding with unauthenticated mode"
          );

          // No stored username, just set the session mode and try to load
          setSessionMode(urlParamSessionMode as SessionMode);

          // Build the repository URL
          let publicRepoURL = `https://github.com/${owner}/${repo}`;
          if (branch) {
            publicRepoURL += `/tree/${branch}`;
          }

          // For session=github without stored credentials, still try to load as authenticated
          // (user might have just been redirected from GitHub OAuth)
          const isAuthenticated = urlParamSessionMode === "github";

          loadPublicRepo(publicRepoURL, isAuthenticated).catch((error) => {
            logger.debug.warn(
              `Repository loading failed in ${urlParamSessionMode} mode:`,
              error
            );
            if (error.message === "PRIVATE_REPOSITORY") {
              logger.debug.info(
                "Repository is private, showing GitHub login dialog"
              );
              // Store the original URL for restoration after GitHub app installation
              sessionStorage.setItem(
                "originalCodeCanvasUrl",
                window.location.href
              );
              useStore.getState().setShowGitHubLoginSpotlight(true);
            }
          });
        }
      } else {
        // No repo/owner specified, just set the session mode
        setSessionMode(urlParamSessionMode as SessionMode);
      }
    } else if (urlParamSessionMode === "local") {
      // For local session, just set the mode
      setSessionMode("local");
    }

    useStore.getState().setURLParamParsingDone(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // loads the diagram only after drawio is initialized
  useEffect(() => {
    if (landingPageLoaded && doLoadDiagram) {
      loadDiagram();
    }
  }, [landingPageLoaded, doLoadDiagram]);

  // useEffect to identify changes in repoData for actions
  // may need to be extended further if we go forward with this pattern
  // without any issues in performance
  // currently only used for update and removals to fuse document db
  useEffect(() => {
    let pathsToUpdate = [];
    for (const key in repoData) {
      if (
        repoData[key] !== historicalRepoData.current[key] &&
        key !== "fileContent"
      ) {
        pathsToUpdate.push(key);
      }
    }
    if (pathsToUpdate.length >= 1) {
      removeFromFuse(pathsToUpdate);
      addToFuse(pathsToUpdate);
    }
  }, [repoData, addToFuse, removeFromFuse]);

  // useEffect to store history of repoData
  useEffect(() => {
    if (repoData) {
      historicalRepoData.current = repoData;
    }
  }, [repoData]);
  // set debug mode from url param
  useEffect(() => {
    const urlParamDebugMode = getURLParam(window, "debugMode");
    if (urlParamDebugMode === "debug" || urlParamDebugMode === "verbose") {
      useStore.getState().setDebugMode(urlParamDebugMode);
    }
  }, []);

  // Listens to keydown events and propagates them to the iframe
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Track user interactions (clicks) for EmailDialog trigger
  useEffect(() => {
    const handleUserInteraction = (event) => {
      // Only count clicks from the user, not programmatic events
      if (event.isTrusted && incrementUserEventCount) {
        incrementUserEventCount();
      }
    };

    // Track various user interaction events
    window.addEventListener("click", handleUserInteraction);
    window.addEventListener("keydown", handleUserInteraction);
    window.addEventListener("scroll", handleUserInteraction);

    return () => {
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
      window.removeEventListener("scroll", handleUserInteraction);
    };
  }, [incrementUserEventCount]);

  // Auto-show floating step description window when simulation is selected
  useEffect(() => {
    const simulationsState = useStore.getState().simulationsState;
    const currentSimulation =
      simulationsState?.simulations?.[selectedSimulationKey];

    // Show floating window if:
    // 1. A simulation is selected
    // 2. The simulation has simulation steps (not just a usecase)
    // 3. Not currently editing descriptions (to avoid conflict with the embedded description editor)
    if (
      selectedSimulationKey &&
      currentSimulation &&
      currentSimulation.simSteps &&
      currentSimulation.simSteps.length > 0 &&
      isEditingSimulations !== "Description"
    ) {
      setFloatingStepDescriptionVisible(true);
    } else {
      setFloatingStepDescriptionVisible(false);
    }
  }, [
    selectedSimulationKey,
    isEditingSimulations,
    setFloatingStepDescriptionVisible,
  ]);

  useStateChange(useStore.getState());

  useEffect(() => {
    const redirectURL = sessionStorage.getItem("redirectUrl");

    // Check if we're on a simple GitHub OAuth redirect without repository parameters
    const currentUrl = new URL(window.location.href);
    const currentParams = new URLSearchParams(currentUrl.search);
    const hasOnlySessionParam =
      currentParams.get("session") === "github" &&
      !currentParams.get("repo") &&
      !currentParams.get("owner");

    if (redirectURL) {
      // clear redirect url from session storage
      sessionStorage.removeItem("redirectUrl");

      // Parse the redirect URL to update session parameter
      try {
        const url = new URL(redirectURL);
        const urlParams = new URLSearchParams(url.search);

        // Update session parameter from unauthenticatedGithub to github
        if (urlParams.get("session") === "unauthenticatedGithub") {
          urlParams.set("session", "github");
          url.search = urlParams.toString();

          // Redirect to the updated URL
          window.location.assign(url.toString());
        } else {
          // redirect to the original url
          window.location.assign(redirectURL);
        }
      } catch (error) {
        logger.error("Error parsing redirect URL:", error);
        // Fallback to original redirect
        window.location.assign(redirectURL);
      }
    } else if (hasOnlySessionParam) {
      // We're on a GitHub OAuth redirect without repo parameters
      // Check if there's an original URL stored (for first-time app installations)
      const originalUrl = sessionStorage.getItem("originalCodeCanvasUrl");

      if (originalUrl) {
        logger.debug.info(
          "Restoring original URL after GitHub app installation:",
          originalUrl
        );
        sessionStorage.removeItem("originalCodeCanvasUrl");

        // Check if it's a CodeCanvas-style URL (e.g., /owner/repo)
        try {
          const url = new URL(originalUrl);

          // If it's a CodeCanvas-style URL, redirect directly
          if (url.pathname.match(/^\/[^\/]+\/[^\/]+$/)) {
            window.location.assign(originalUrl);
          } else {
            // For standard URLs, update session parameter
            const urlParams = new URLSearchParams(url.search);
            urlParams.set("session", "github");
            url.search = urlParams.toString();

            // Redirect to the updated original URL
            window.location.assign(url.toString());
          }
        } catch (error) {
          logger.error("Error parsing original URL:", error);
          // Fallback - just redirect to the original URL
          window.location.assign(originalUrl);
        }
      }
    }
  }, []);

  // Extracted and renamed function for toggling irrelevant cells visibility
  const hideIrrelevantCellsToggleAction = async (
    hideIrrelevantCells: boolean,
    considerParentsAsRelevantCells: boolean
  ) => {
    const drawioResponse = await useStore
      .getState()
      .postToDrawioWaitForResponse({
        action: "HIDE_IRRELEVANT_CELLS_TOGGLE_ACTION",
        data: {
          hideIrrelevantCells,
          considerParentsAsRelevantCells,
        },
      });
    if (drawioResponse?.status === "ERROR") {
      logger.error(
        "Error: HIDE_IRRELEVANT_CELLS_TOGGLE_ACTION",
        drawioResponse
      );
    }
  };

  useEffect(() => {
    if (hideIrrelevantCells) {
      hideIrrelevantCellsToggleAction(
        hideIrrelevantCells,
        considerParentsAsRelevantCells
      );
    } else {
      hideIrrelevantCellsToggleAction(false, false);
    }
  }, [hideIrrelevantCells, considerParentsAsRelevantCells]);

  useEffect(() => {
    if (isEditingSimulations !== "" || !selectedSimulationKey) {
      useStore.getState().setHideIrrelevantCells(false);
    }
  }, [
    isEditingSimulations,
    selectedSimulationKey,
    showHideIrrelevantCellsToggle,
  ]);

  // Sync debugMode to drawio
  useEffect(() => {
    if (!drawioClient || !landingPageLoaded) return;
    if (debugMode === "debug" || debugMode === "verbose") {
      useStore.getState().postToDrawioWaitForResponse({
        action: "SET_DEBUG_MODE",
        data: { debugMode: debugMode },
      });
    }
  }, [drawioClient, debugMode, landingPageLoaded]);

  // Enhanced repository loading with authentication flow
  const handleRepositoryLoadWithAuthentication = async (
    repoURL: string,
    repoName: string
  ) => {
    logger.debug.info(
      "Starting enhanced repository loading with authentication flow",
      {
        repoURL,
        repoName,
        currentSessionMode: useStore.getState().session.mode,
        isLoggedIn: useStore.getState().session.isLoggedIn,
      }
    );

    // Check if we're already logged in (from URL parameter handling)
    const currentState = useStore.getState();
    if (
      currentState.session.isLoggedIn &&
      currentState.session.mode === "github"
    ) {
      logger.debug.info(
        "User is already logged in with GitHub session, loading repository directly"
      );

      try {
        await loadPublicRepo(repoURL, true);
        logger.debug.info(
          "Repository loaded successfully with existing authentication"
        );
      } catch (error) {
        logger.debug.warn(
          "Repository loading failed with existing authentication:",
          error
        );

        if (error.message === "PRIVATE_REPOSITORY") {
          logger.debug.info(
            "Private repository error for authenticated user, showing GitHub login spotlight for re-authentication"
          );
          // Store the original URL for restoration after GitHub app installation
          sessionStorage.setItem("originalCodeCanvasUrl", window.location.href);
          useStore.getState().setShowGitHubLoginSpotlight(true);
        } else {
          useStore
            .getState()
            .setErrorNotification(
              "Failed to load repository. Please check the URL and permissions."
            );
        }
      }
      return;
    }

    // For CodeCanvas URLs, check authentication first
    try {
      const authResult = await checkPassportAuthentication();

      if (authResult.isAuthenticated) {
        logger.debug.info(
          "User is authenticated via passport.js, loading with authentication"
        );

        // Update store with authenticated user info
        if (authResult.user) {
          useStore.getState().setLoggedIn(true);
          useStore.getState().setSessionMode("github");
          setURLParam(window, "session", "github");

          if (authResult.user.access_token) {
            localStorage.setItem("accessToken", authResult.user.access_token);
          }
          if (authResult.user.username || authResult.user.login) {
            localStorage.setItem(
              "username",
              authResult.user.username || authResult.user.login
            );
            useStore
              .getState()
              .setUsername(authResult.user.username || authResult.user.login);
          }
        }

        // Try to load with authentication
        try {
          await loadPublicRepo(repoURL, true);
          logger.debug.info(
            "Repository loaded successfully with authentication"
          );
        } catch (error) {
          if (error.message === "PRIVATE_REPOSITORY") {
            // Store the original URL for restoration after GitHub app installation
            sessionStorage.setItem(
              "originalCodeCanvasUrl",
              window.location.href
            );
            useStore.getState().setShowGitHubLoginSpotlight(true);
          } else {
            useStore
              .getState()
              .setErrorNotification(
                "Failed to load repository. Please check the URL and permissions."
              );
          }
        }
      } else {
        // Not authenticated, try as public
        logger.debug.info(
          "User not authenticated, trying as public repository"
        );
        setURLParam(window, "session", "unauthenticatedGithub");

        try {
          await loadPublicRepo(repoURL, false);
          logger.debug.info("Repository loaded successfully as public");
        } catch (error) {
          if (error.message === "PRIVATE_REPOSITORY") {
            logger.debug.info(
              "Repository is private, showing GitHub login spotlight"
            );
            // Store the original URL for restoration after GitHub app installation
            sessionStorage.setItem(
              "originalCodeCanvasUrl",
              window.location.href
            );
            useStore.getState().setShowGitHubLoginSpotlight(true);
          } else {
            useStore
              .getState()
              .setErrorNotification(
                "Failed to load repository. Please check the URL and try again."
              );
          }
        }
      }
    } catch (error) {
      logger.debug.warn("Error checking authentication:", error);

      // On error, try as public
      try {
        await loadPublicRepo(repoURL, false);
      } catch (loadError) {
        if (loadError.message === "PRIVATE_REPOSITORY") {
          // Store the original URL for restoration after GitHub app installation
          sessionStorage.setItem("originalCodeCanvasUrl", window.location.href);
          useStore.getState().setShowGitHubLoginSpotlight(true);
        } else {
          useStore
            .getState()
            .setErrorNotification(
              "Failed to load repository. Please check the URL and try again."
            );
        }
      }
    }
  };

  // Handlers for model selection dialog
  const handleModelSelectionConfirm = (selectedModel: string) => {
    logger.info(`Model selection confirmed: ${selectedModel}`);
    setSelectedModel(selectedModel);
    setShowOnboardingModelSelectionDialog(false);
  };

  const handleModelSelectionClose = () => {
    logger.info("Model selection dialog closed");
    setShowOnboardingModelSelectionDialog(false);
  };

  // Warn user if not using Chrome
  useEffect(() => {
    // Check if the user's browser is not Chrome
    if (!window.navigator.userAgent.includes("Chrome") && !isMobile) {
      // Display a warning message
      alert(
        "This application is best viewed in Google Chrome and is supported only on desktop. Please consider switching to this browser and platform for the best experience."
      );
    }
  }, []);
  // Mobile detection
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  if (isMobile) {
    return <MobileLandingPage />;
  }

  return (
    <div className="App">
      {debugMode && <DevTools />}
      <Header />
      <iframe
        data-testid="drawio-iframe"
        id="drawio-iframe"
        className="DrawIOFrame"
        ref={iFrameRef}
        src={`${REACT_APP_DRAWIO_URL}/index.html`}
        title="Child iFrame"
      ></iframe>
      <AutoDiagramGenerationOverlay />
      <SimulationGenerationOverlay />
      <CellInsertionOverlay />
      <SourceDoc />
      {checkRunImpactedCells?.length > 0 && <CheckRunView />}
      <EditButton />
      <BackToLandingPageButton />
      <ShapesButton />
      <ToolsButton />
      <ModelSelectionButton />
      <ModelSelectionDialog
        open={showOnboardingModelSelectionDialog}
        onClose={handleModelSelectionClose}
        onConfirm={handleModelSelectionConfirm}
      />
      <Dialogs />
      <AlternativeLandingComponent />
      <GuidedWizard steps={onboardingConfig} />
      <GuidedWizard
        steps={simulationOnboardingConfig}
        run={isSimulationOnboardingActive}
        isSimulationOnboarding={true}
      />
      <DiagramHighlight />
      <CenterGraphButton />
      <StatsWidget isVisible={statsWidgetVisible} />
      <OnboardingWorkflow />
      <GlobalLoadingStatusBar />
      <FloatingStepDescriptionWindow
        isVisible={floatingStepDescriptionVisible}
        onClose={() => setFloatingStepDescriptionVisible(false)}
      />
      <GitHubLoginSpotlight />
    </div>
  );
};

export default App;
